import type { OrderCancellationCode } from "@loot-os/core/db/schema";
import { Button } from "@loot-os/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@loot-os/ui/components/dialog";
import { Label } from "@loot-os/ui/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import { toast } from "@loot-os/ui/components/sonner";
import { Textarea } from "@loot-os/ui/components/textarea";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  cancellationReasonError,
  FULFILLER_CANCELLATION_CODES,
  type FulfillerCancellationCode,
} from "../lib/orderActions";
import { useTRPC } from "../lib/trpc";

const CODE_LABELS: Record<FulfillerCancellationCode, string> = {
  PRICE_EXCEEDED: "PRICE_EXCEEDED — the live buy price is above the snapshot",
  OUT_OF_STOCK: "OUT_OF_STOCK — the item or the shopper's variant is gone",
};

/**
 * The one action with required input: a coded cause a tenant branches on and
 * the fulfiller's own note. Cancelling a line instead of the order is the same
 * form against orders.cancelLine, so both live here.
 */
export function OrderCancelDialog({
  orderId,
  line,
}: {
  orderId: string;
  /** Set to cancel one line; the order itself is cancelled when this is absent. */
  line?: { id: string; isLastActive: boolean };
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState<FulfillerCancellationCode>("PRICE_EXCEEDED");
  const [reason, setReason] = useState("");

  const done = (message: string) => {
    toast.success(message);
    void queryClient.invalidateQueries({ queryKey: trpc.platform.fulfillment.orders.pathKey() });
    setOpen(false);
    setReason("");
  };

  const cancelOrder = useMutation(
    trpc.platform.fulfillment.orders.cancel.mutationOptions({
      onSuccess: (result) =>
        done(
          "shippingFeeRefunded" in result
            ? "Order cancelled and the shipping fee refunded."
            : "Order cancelled.",
        ),
    }),
  );
  const cancelLine = useMutation(
    trpc.platform.fulfillment.orders.cancelLine.mutationOptions({
      onSuccess: () => done("Line cancelled."),
    }),
  );

  const isPending = cancelOrder.isPending || cancelLine.isPending;
  const reasonError = cancellationReasonError(reason);

  const submit = () => {
    if (reasonError) return;
    // The dashboard never imports the schema module, so the picker holds the enum's values as plain strings.
    const input = { id: orderId, code: code as OrderCancellationCode, reason: reason.trim() };
    if (line) cancelLine.mutate({ ...input, lineId: line.id });
    else cancelOrder.mutate(input);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !isPending && setOpen(next)}>
      <DialogTrigger
        render={
          <Button
            type="button"
            variant={line ? "ghost" : "destructive"}
            size={line ? "sm" : undefined}
          />
        }
      >
        {line ? "Cancel line" : "Cancel order"}
      </DialogTrigger>
      <DialogContent showCloseButton={!isPending}>
        <DialogHeader>
          <DialogTitle>{line ? "Cancel this line?" : "Cancel this order?"}</DialogTitle>
          <DialogDescription>
            The tenant reads the code back on the order; the note is for whoever looks at it later.
            {line?.isLastActive
              ? " This is the last line still active, so cancelling it cancels the whole order."
              : ""}
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div className="grid gap-2">
            <Label htmlFor="cancellation-code">Cause</Label>
            <Select
              value={code}
              disabled={isPending}
              onValueChange={(next) => setCode(next as FulfillerCancellationCode)}
            >
              <SelectTrigger id="cancellation-code">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FULFILLER_CANCELLATION_CODES.map((member) => (
                  <SelectItem key={member} value={member}>
                    {CODE_LABELS[member]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="cancellation-reason">Note</Label>
            <Textarea
              id="cancellation-reason"
              value={reason}
              disabled={isPending}
              aria-invalid={reasonError !== undefined}
              onChange={(e) => setReason(e.target.value)}
            />
            {reasonError ? <p className="text-sm text-destructive">{reasonError}</p> : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={isPending}
              onClick={() => setOpen(false)}
            >
              Back
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={isPending || reasonError !== undefined}
            >
              {isPending ? "Cancelling…" : line ? "Cancel line" : "Cancel order"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
